// AlertFactsPanel — renders the pipeline-owned `alert_facts_json` on an alert row.
//
// Shared by AlertsPage and TestRunResultsPage (CLAUDE.md rule #13 parity: one render,
// two pages). RecentEventsPage has no alert rows, so it has no consumer here.
//
// Returns null when the row carries no usable facts; the caller then keeps its
// existing display (for inbox rules, the parameters table built by
// exchangeRuleHelpers). All parsing rules live in alertFactsHelpers.js.
//
// Values are plain text — React escapes them, and nothing here builds markup from a
// value. `break-words` rather than `break-all` so a sender list wraps at word
// boundaries while a long address still cannot overflow the row.

import React, { useMemo } from 'react';
import Badge from '../ui/Badge';
import { parseAlertFacts, buildLocalGroupChangeSummary } from './alertFactsHelpers';

const KIND_TITLES = {
  inbox_rule: 'Inbox Rule',
  local_group_change: 'Local Group Change',
};

function SummaryRow({ label, children }) {
  return (
    <tr className="align-top">
      <td className="py-1 pr-6 text-gray-400 w-40">{label}</td>
      <td className="py-1 text-gray-200 break-words">{children}</td>
    </tr>
  );
}

function FactsTable({ groups }) {
  return (
    <table className="w-full max-w-3xl text-xs">
      {groups.map((g) => (
        <tbody key={g.group}>
          <tr>
            <td colSpan={2} className="pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              {g.title}
            </td>
          </tr>
          {g.facts.map((f) => (
            <tr key={f.key} className="border-b border-gray-800/40 last:border-0 align-top">
              <td className="py-1 pr-6 text-gray-400 w-56">{f.label}</td>
              <td className="py-1 text-gray-300 break-words">{f.value}</td>
            </tr>
          ))}
        </tbody>
      ))}
    </table>
  );
}

// local_group_change in sentences. The facts table is not shown: the summary states it, and the
// SIDs are in the Raw Event JSON panel.
function LocalGroupChangeSummary({ summary }) {
  return (
    <>
      {summary.chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {summary.chips.map((f) => (
            <Badge key={f.flag} tone={f.tone}>{f.label}</Badge>
          ))}
        </div>
      )}
      <table className="w-full max-w-3xl text-sm">
        <tbody>
          <SummaryRow label="What happened">{summary.whatHappened}</SummaryRow>
          {summary.doneBy && <SummaryRow label="Done by">{summary.doneBy}</SummaryRow>}
          <SummaryRow label="Effect">
            <p>{summary.effect}</p>
            {summary.members?.note && <p className="mt-1 text-gray-400">{summary.members.note}</p>}
            {summary.members?.list && (
              <div className="mt-1">
                <p className="text-gray-400">{summary.members.heading}:</p>
                <p className="text-gray-300 text-xs">{summary.members.list}</p>
                {summary.members.source && <p className="text-gray-500 text-xs mt-0.5">{summary.members.source}</p>}
              </div>
            )}
          </SummaryRow>
          {summary.domainJoin && (
            <SummaryRow label="Likely a domain join">
              <span className="text-sky-300">{summary.domainJoin}</span>
            </SummaryRow>
          )}
        </tbody>
      </table>
    </>
  );
}

export default function AlertFactsPanel({ alertFactsJson, className = '' }) {
  const facts = useMemo(() => parseAlertFacts(alertFactsJson), [alertFactsJson]);
  const lgcSummary = useMemo(() => buildLocalGroupChangeSummary(facts), [facts]);
  if (!facts) return null;

  if (lgcSummary) {
    return (
      <div className={className}>
        <p className="font-semibold text-gray-400 uppercase tracking-wide text-xs mb-2">
          {KIND_TITLES[facts.kind]}
        </p>
        <LocalGroupChangeSummary summary={lgcSummary} />
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="font-semibold text-gray-400 uppercase tracking-wide text-xs mb-2">
        {KIND_TITLES[facts.kind] || 'Alert Details'}
      </p>

      {facts.headline && (
        <p className="text-sm text-gray-200 mb-2 break-words">{facts.headline}</p>
      )}

      {facts.flags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {facts.flags.map((f) => (
            <Badge key={f.flag} tone={f.tone}>{f.label}</Badge>
          ))}
        </div>
      )}

      {facts.contentless && facts.groups.length === 0 && (
        <p className="text-xs text-gray-500 italic mb-2">
          Outlook re-synchronised the rule without its content; this event does not record what the rule does.
        </p>
      )}

      {facts.groups.length > 0 && <FactsTable groups={facts.groups} />}
    </div>
  );
}
